import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BedDouble,
  CheckCircle2,
  Clock,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { api, money, shortDate } from "@/util";

const ROOM_TYPES = ["Single", "Double", "Double Sea View", "Triple", "Family", "Suite", "Deluxe"];
const STATUSES = ["Onaylandı", "Bekliyor", "İptal"];

function emptyForm() {
  return { customer_id: "", customer_name: "", phone: "", pax: 2, room_type: "Double", room_no: "", amount: 0, status: "Onaylandı", notes: "" };
}

function ReservationForm({ initial, customers, submitting, error, onCancel, onSubmit }) {
  const [form, setForm] = useState(initial || emptyForm());
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCustomerPick = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) {
      setForm((prev) => ({ ...prev, customer_id: id, customer_name: c.name, phone: c.phone || "" }));
    } else {
      setForm((prev) => ({ ...prev, customer_id: "" }));
    }
  };

  return (
    <>
      <div className="form-grid">
        <label className="form-field span-2">
          <span>Kayıtlı müşteri (opsiyonel)</span>
          <select value={form.customer_id || ""} onChange={(e) => handleCustomerPick(e.target.value)} data-testid="form-reservation-customer-select">
            <option value="">— Yeni misafir (manuel) —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.phone || "—"}</option>
            ))}
          </select>
        </label>
        <label className="form-field span-2">
          <span>Ad Soyad</span>
          <input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} required data-testid="form-reservation-name" />
        </label>
        <label className="form-field">
          <span>Telefon</span>
          <input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="form-reservation-phone" />
        </label>
        <label className="form-field">
          <span>Kişi sayısı</span>
          <input type="number" min="1" value={form.pax} onChange={(e) => update("pax", e.target.value)} required data-testid="form-reservation-pax" />
        </label>
        <label className="form-field">
          <span>Oda tipi</span>
          <select value={form.room_type} onChange={(e) => update("room_type", e.target.value)} data-testid="form-reservation-room-type">
            {ROOM_TYPES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span>Oda no</span>
          <input value={form.room_no} onChange={(e) => update("room_no", e.target.value)} placeholder="Örn. 214" data-testid="form-reservation-room-no" />
        </label>
        <label className="form-field">
          <span>Tutar (₺)</span>
          <input type="number" step="50" value={form.amount} onChange={(e) => update("amount", e.target.value)} data-testid="form-reservation-amount" />
        </label>
        <label className="form-field">
          <span>Durum</span>
          <select value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="form-reservation-status">
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="form-field span-2">
          <span>Not</span>
          <textarea rows="2" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="form-reservation-notes" />
        </label>
      </div>
      {error && <div className="auth-error" style={{ marginTop: 12 }} data-testid="reservation-form-error">{error}</div>}
      <div className="form-actions">
        <button className="outline-button" onClick={onCancel} data-testid="reservation-form-cancel">İptal</button>
        <button
          className="primary-button"
          onClick={() => onSubmit({
            customer_id: form.customer_id || null,
            customer_name: form.customer_name,
            phone: form.phone,
            pax: Number(form.pax || 1),
            room_type: form.room_type,
            room_no: form.room_no,
            amount: Number(form.amount || 0),
            status: form.status,
            notes: form.notes,
          })}
          disabled={submitting}
          data-testid="reservation-form-submit"
        >
          {submitting ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </>
  );
}

export default function TourDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isManager = user?.role === "Yönetici";

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        api.get(`/tours/${id}`),
        api.get("/customers"),
      ]);
      setData(t.data);
      setCustomers(c.data);
    } catch (err) {
      setData({ error: true });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (payload) => {
    setSubmitting(true);
    setError("");
    try {
      if (editing) {
        await api.patch(`/reservations/${editing.id}`, payload);
      } else {
        await api.post(`/tours/${id}/reservations`, payload);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Rezervasyon kaydedilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  const removeReservation = async (res) => {
    if (!window.confirm(`${res.customer_name} rezervasyonu silinsin mi?`)) return;
    await api.delete(`/reservations/${res.id}`);
    await load();
  };

  if (!data) return <div className="empty-page" data-testid="tour-detail-loading"><p>Yükleniyor…</p></div>;
  if (data.error) return (
    <div className="empty-page">
      <h2>Tur bulunamadı</h2>
      <Link to="/turlar" className="primary-button" style={{ display: "inline-flex", marginTop: 10 }}>← Tur listesine dön</Link>
    </div>
  );

  const { tour, reservations, pax_total, paid } = data;
  const occupancy = Math.min(100, Math.round((pax_total / Math.max(tour.capacity, 1)) * 100));
  const freeSeats = Math.max(0, tour.capacity - pax_total);
  const roomGroups = reservations.reduce((acc, r) => {
    const k = r.room_no || r.room_type || "Atanmamış";
    acc[k] = acc[k] || { room: k, type: r.room_type, pax: 0, people: [] };
    acc[k].pax += r.pax;
    acc[k].people.push(r.customer_name);
    return acc;
  }, {});

  return (
    <section className="page-section" data-testid="tour-detail-page">
      <button className="outline-button back-button" onClick={() => navigate("/turlar")} data-testid="tour-back-button">
        <ArrowLeft size={14} /> Turlara dön
      </button>

      <div className="section-header" style={{ marginBottom: 18 }}>
        <div>
          <span className="eyebrow">OPERASYON · {tour.status.toUpperCase()}</span>
          <h2 data-testid="tour-detail-name">{tour.name}</h2>
          <p>{tour.destination} · {shortDate(tour.start_date)} – {shortDate(tour.end_date)} · {tour.operator || "Operatör atanmamış"}</p>
        </div>
        <button className="primary-button" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-reservation-button">
          <UserPlus size={16} /> Rezervasyon ekle
        </button>
      </div>

      <div className="finance-summary">
        <div className="finance-chip neutral" data-testid="tour-capacity-chip">
          <small>Doluluk</small>
          <b>{pax_total}/{tour.capacity}</b>
        </div>
        <div className="finance-chip positive" data-testid="tour-free-chip">
          <small>Boş kontenjan</small>
          <b>{freeSeats} kişi</b>
        </div>
        <div className="finance-chip positive" data-testid="tour-paid-chip">
          <small>Onaylı tahsilat</small>
          <b>{money(paid)}</b>
        </div>
        <div className="finance-chip neutral" data-testid="tour-revenue-chip">
          <small>Planlanan gelir</small>
          <b>{money(tour.revenue)}</b>
        </div>
      </div>

      <div className="occupancy-bar" aria-label="Doluluk">
        <div style={{ width: `${occupancy}%` }} />
        <span>{occupancy}% dolu</span>
      </div>

      <div className="dashboard-grid secondary" style={{ marginTop: 22 }}>
        <section className="panel" data-testid="reservations-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">REZERVASYONLAR</span>
              <h3>Katılımcı listesi</h3>
            </div>
            <button className="icon-button" onClick={() => { setEditing(null); setFormOpen(true); }} title="Rezervasyon ekle" data-testid="reservations-add-icon">
              <Plus size={17} />
            </button>
          </div>
          {reservations.length ? (
            <div className="table-wrap flush">
              <table className="data-table" data-testid="reservations-table">
                <thead>
                  <tr>
                    <th>Misafir</th>
                    <th>Oda</th>
                    <th>Kişi</th>
                    <th>Durum</th>
                    <th className="right">Tutar</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r, idx) => (
                    <tr key={r.id} data-testid={`reservation-row-${idx}`}>
                      <td>
                        <b>{r.customer_name}</b>
                        <small>{r.phone || "—"}</small>
                      </td>
                      <td>
                        <b>{r.room_type}</b>
                        <small>{r.room_no ? `No ${r.room_no}` : "Oda atanmamış"}</small>
                      </td>
                      <td className="muted">{r.pax}</td>
                      <td>
                        <span className={`pill ${r.status === "Onaylandı" ? "success" : r.status === "İptal" ? "warning" : "info"}`}>
                          {r.status === "Onaylandı" ? <CheckCircle2 size={11} /> : r.status === "İptal" ? <X size={11} /> : <Clock size={11} />} {r.status}
                        </span>
                      </td>
                      <td className="right"><b>{money(r.amount)}</b></td>
                      <td className="actions">
                        <button className="icon-button small" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`reservation-edit-${idx}`} title="Düzenle">
                          <Pencil size={13} />
                        </button>
                        {isManager && (
                          <button className="icon-button small danger" onClick={() => removeReservation(r)} data-testid={`reservation-delete-${idx}`} title="Sil">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-inline">Henüz rezervasyon yok. Üstteki buton ile ilk katılımcıyı ekle.</p>
          )}
        </section>

        <section className="panel" data-testid="room-plan-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">ODA PLANI</span>
              <h3>Kontenjan dağılımı</h3>
            </div>
          </div>
          {Object.values(roomGroups).length ? (
            <div className="room-list">
              {Object.values(roomGroups).map((g) => (
                <div className="room-row" key={g.room} data-testid={`room-${g.room}`}>
                  <div className="room-icon"><BedDouble size={16} /></div>
                  <div className="room-info">
                    <b>{g.type}</b>
                    <small>Oda {g.room} · {g.pax} kişi</small>
                  </div>
                  <div className="room-people" title={g.people.join(", ")}>
                    {g.people.join(", ")}
                  </div>
                </div>
              ))}
              <div className="room-row free">
                <div className="room-icon"><Plus size={16} /></div>
                <div className="room-info">
                  <b>Boş kontenjan</b>
                  <small>{freeSeats} kişilik yer kaldı</small>
                </div>
              </div>
            </div>
          ) : (
            <p className="empty-inline">Oda dağılımı için rezervasyon ekle.</p>
          )}
        </section>
      </div>

      {formOpen && (
        <div className="modal-backdrop" onClick={() => { setFormOpen(false); setEditing(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="reservation-modal">
            <button className="modal-close" onClick={() => { setFormOpen(false); setEditing(null); }} data-testid="reservation-form-close">
              <X size={18} />
            </button>
            <span className="eyebrow">{editing ? "REZERVASYONU DÜZENLE" : "YENİ REZERVASYON"}</span>
            <h2 className="form-title">{tour.name}</h2>
            <p>Oda, kontenjan ve ödeme bilgisini doldur.</p>
            <ReservationForm
              initial={editing || null}
              customers={customers}
              submitting={submitting}
              error={error}
              onCancel={() => { setFormOpen(false); setEditing(null); }}
              onSubmit={submit}
            />
          </div>
        </div>
      )}
    </section>
  );
}
